import { UzButton, UzInput, UzSpinner } from '@utazzo/ui';
import { MapPin, Search } from 'lucide-react';
import { useRef, useState } from 'react';

import type { CityListItem } from '@utazzo/shared';

export interface CitySearchPanelProps {
  /// Currently-selected city id (form state). Null when empty.
  value: string | null;
  /// Label to show for the current selection without having to refetch -
  /// usually `"<name> (<isoCode>)"`. Pass `null` when value is null.
  valueLabel: string | null;
  /// Fires with both id and label so the parent form can store the id
  /// AND echo the label without an extra fetch.
  onChange: (next: { id: string | null; label: string | null }) => void;
  /// Field label shown above the input.
  label: string;
  /// Hint text rendered when no search term is entered. Used to surface
  /// caller-specific context, e.g. "Pick a lead category first" or
  /// "Showing every active travel destination".
  emptyHint: string;
  /// Server-side search results (already deduped + flattened across
  /// pages by `useCitySearchForLeads`).
  cities: ReadonlyArray<CityListItem>;
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  /// Fires when the operator clicks "Load more".
  onLoadMore?: () => void;
  /// Fires whenever the search term changes - the caller drives the
  /// hook query off this value.
  term: string;
  onTermChange: (term: string) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  /// Override the input placeholder; defaults to "Search by name or IATA".
  placeholder?: string;
  /// Stable name attribute - used by react-hook-form's scroll-to-error.
  fieldName?: string;
}

/**
 * Shared selection + search dropdown used by both the Departure city
 * and Destination city pickers on the lead form.
 *
 * Two visual modes:
 *  - **Selected**: a compact tile showing the chosen city + a "Change"
 *    button. Same shape as `CustomerPicker` so the form reads
 *    consistent.
 *  - **Searching**: a search input + a floating results list. The
 *    panel opens immediately on focus and lists the first 20 active
 *    cities; typing in the search box narrows the same list (the
 *    server runs a prefix match on city name / IATA / custom code
 *    (see `searchMode: 'prefix'`). A "Load more" button at the bottom of the list
 *    advances the cursor one page at a time (`hasMore` from the hook).
 */
export const CitySearchPanel = ({
  value,
  valueLabel,
  onChange,
  label,
  emptyHint,
  cities,
  isLoading,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  term,
  onTermChange,
  disabled,
  required,
  error,
  placeholder = 'Search by name or IATA',
  fieldName = 'city-search',
}: CitySearchPanelProps): React.JSX.Element => {
  const [focused, setFocused] = useState(false);
  // Blur-then-click guard: clicking a result row briefly blurs the
  // input, which would otherwise close the panel before the click
  // handler fires. We delay the close by one tick so the row click
  // still lands.
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Panel opens as soon as the input is focused, with no min-chars
  // gate. An empty term server-side returns the first page of active
  // cities (20 rows; see `useCitySearchForLeads`); the operator can
  // start typing to narrow.
  const open = focused;

  if (value && valueLabel) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
        <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white p-2">
          <MapPin className="h-4 w-4 shrink-0 text-zinc-400" />
          <span className="flex-1 truncate text-sm font-medium text-zinc-900">{valueLabel}</span>
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => onChange({ id: null, label: null })}
            disabled={disabled}
          >
            Change
          </UzButton>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <div className="relative">
        <UzInput
          name={fieldName}
          placeholder={placeholder}
          leftAdornment={<Search className="h-4 w-4" />}
          value={term}
          onChange={(e) => onTermChange(e.target.value)}
          onFocus={() => {
            if (blurTimer.current) clearTimeout(blurTimer.current);
            setFocused(true);
          }}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setFocused(false), 150);
          }}
          disabled={disabled}
          autoComplete="off"
          hint={term.trim().length === 0 ? emptyHint : undefined}
        />
        {/* Floating panel - absolute so it overlays adjacent grid
            columns instead of stretching the form's row height. */}
        {open && (
          <div className="absolute top-full right-0 left-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-zinc-200 bg-white shadow-lg">
            {isLoading && cities.length === 0 ? (
              <div className="flex items-center gap-2 p-3 text-sm text-zinc-500">
                <UzSpinner size="sm" /> Searching…
              </div>
            ) : cities.length === 0 ? (
              <div className="p-3 text-sm text-zinc-500">No matches. Try a different search.</div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {cities.map((c) => {
                  const labelText = c.country?.isoCode
                    ? `${c.name} (${c.country.isoCode.toUpperCase()})`
                    : c.name;
                  const subtitle = [c.state?.name, c.country?.name].filter(Boolean).join(' · ');
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="flex w-full items-start gap-3 p-2.5 text-left hover:bg-zinc-50"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          onChange({ id: c.id, label: labelText });
                          onTermChange('');
                          setFocused(false);
                        }}
                      >
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm font-medium text-zinc-900">
                            {labelText}
                          </span>
                          {subtitle && (
                            <span className="truncate text-xs text-zinc-500">{subtitle}</span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
                {hasMore && (
                  <li>
                    <button
                      type="button"
                      className="flex w-full items-center justify-center gap-2 p-2 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-60"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onLoadMore?.()}
                      disabled={isLoadingMore}
                    >
                      {isLoadingMore ? (
                        <>
                          <UzSpinner size="sm" /> Loading…
                        </>
                      ) : (
                        'Load more'
                      )}
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};
